import type { ExitPhase } from '@/hooks/useListItemExitAnimation';
import { cn } from '@/utils/classUtils';
import type { FC, ReactNode } from 'react';

type ExitCollapseRowProps = {
  /** Current exit phase, or null if the row is not exiting. */
  phase: ExitPhase | null;
  /** Duration of the height-collapse transition in ms. */
  collapseDurationMs: number;
  /** CSS easing for the height collapse. */
  collapseEasing?: string;
  className?: string;
  ariaHidden?: boolean;
  children: ReactNode;
};

/**
 * Wrapper that animates a list row's height from `1fr` to `0fr` when its exit
 * phase reaches `collapse`. Uses grid-template-rows, which transitions cleanly
 * between fractional values without needing to measure content height.
 *
 * Pairs with `useListItemExitAnimation`.
 */
export const ExitCollapseRow: FC<ExitCollapseRowProps> = ({
  phase,
  collapseDurationMs,
  collapseEasing = 'cubic-bezier(0.2, 0.72, 0.22, 1)',
  className,
  ariaHidden,
  children,
}) => {
  const isExiting = phase !== null;

  if (!isExiting) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={cn('grid ease-in-out overflow-hidden', className)}
      style={{
        gridTemplateRows: phase === 'collapse' ? '0fr' : '1fr',
        transition:
          phase === 'collapse'
            ? `grid-template-rows ${collapseDurationMs}ms ${collapseEasing}`
            : undefined,
      }}
      aria-hidden={ariaHidden}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
};
